import React, { useEffect, useRef, useState } from 'react'
import Portal from '~shared/lib/Portal/Portal'
import Dialog from '~shared/lib/Dialog/Dialog'
import { BarcodeReader } from '../../Utils/BarcodeReader'
import { Order } from '../../Types/Order'
import { OrderItem } from '../../Types/OrderItem'
import { useWeightedItemValidator } from './weightedItemValidator'
import kdsStore from '../../Stores/KdsStore'

interface WeightConfirmationModalProps {
  weightConfirmationSource: string
  order: Order
  close: () => void
  onWeightConfirmed?: (order: Order) => Promise<void>
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ValidationError = any

export default function WeightConfirmationModal(props: WeightConfirmationModalProps) {
  const { weightConfirmationSource } = props
  const [items, setItems] = useState(props.order.items.filter(item => item.inThisDepartment))
  const [validationError, setValidationError] = useState<ValidationError>(null)
  const [isTempQtySet, setIsTempQtySet] = useState(false)
  const barcodeReaderRef = useRef(new BarcodeReader())
  const barcodeReader = barcodeReaderRef.current
  const itemsRef = useRef(items)
  itemsRef.current = items
  const elementsRef = useRef<Array<React.RefObject<HTMLInputElement | null>>>(
    props.order.items
      .filter(item => item.inThisDepartment)
      .map(() => React.createRef<HTMLInputElement>())
  )
  const { weightedItemValidator, WeightDiscrepancyModal } = useWeightedItemValidator()

  useEffect(() => {
    // Trim out non-weighted items
    if (items) {
      // Clone items to avoid mutating props/store data
      const clonedItems = items.map(item => {
        if (item.weightRequiresConfirmation && !item.weightConfirmed) {
          return { ...item, tempQty: item.quantity, weightConfirmed: false }
        }
        return { ...item }
      })

      setItems(clonedItems)
      setIsTempQtySet(true)

      if (elementsRef && elementsRef.current) {
        // Find first empty active input element in the ref list and focus on its input
        elementsRef.current.find(el => el.current && !el.current.value)?.current?.focus()
      }
    }

    barcodeReader.startMonitoring(onNewScanResult)

    return () => {
      barcodeReader.stopMonitoring()
    }
  }, [])

  const onNewScanResult = async (barcode: string) => {
    if (barcode) {
      let upc = barcode.substring(0, 7)
      let tempItems = itemsRef.current
      let targetedItem: HTMLInputElement | null = null

      let item = tempItems.filter(item => {
        return item.code == upc
      })[0]

      if (item) {
        const barcodeInfo = await barcodeReader.getItemBarcodeItemInfo(barcode)

        if (barcodeInfo && barcodeInfo.price != null) {
          let weight = await barcodeReader.calculateWeightFromBarcode(
            barcodeInfo.price,
            item.itemPrice
          )

          if (weight) {
            let itemIndex = tempItems.findIndex(x => x.id == item.id)
            let weightNum = parseFloat(weight)

            // Update the required input field
            if (elementsRef.current[itemIndex] && elementsRef.current[itemIndex].current) {
              targetedItem = elementsRef.current[itemIndex].current
              elementsRef.current[itemIndex].current!.value = `${weightNum}`
            }

            setItems(prev =>
              prev.map((i, idx) =>
                idx === itemIndex
                  ? { ...i, quantity: weightNum, weightConfirmed: !isNaN(weightNum) }
                  : i
              )
            )
          }
        }
      }

      // Scanner will always enter a single digit into any currently active field. Remove it
      let activeField = document.activeElement as HTMLInputElement
      if (
        activeField != targetedItem &&
        activeField.value &&
        activeField.value.endsWith(barcode.at(0) ?? '')
      ) {
        activeField.value = activeField.value.substring(0, activeField.value.length - 1)
      }
    }
  }

  const onWeightChange = async (
    value: string,
    item: OrderItem,
    ref: React.RefObject<HTMLInputElement | null>
  ) => {
    let qty = parseFloat(value)
    const hasItem = items.some(orderItem => orderItem.id == item.id)
    if (hasItem) {
      // if weight variation is greater than 30% of the original weight, show a warning
      const weightValid = await weightedItemValidator(item, qty)
      if (!weightValid) {
        if (ref.current) {
          ref.current.value = ''
          ref.current.focus()
        }
        return
      }

      setItems(prev =>
        prev.map(i => (i.id == item.id ? { ...i, quantity: qty, weightConfirmed: !isNaN(qty) } : i))
      )
    }
  }

  const submitWeights = async () => {
    try {
      const status = weightConfirmationSource == 'completeAction' ? 'complete' : null
      let tempItems = items

      // Only send items that have had their qty changed.
      const updatedItems = tempItems.filter(item => {
        return (
          item.inThisDepartment && item.weightType != 'Fixed' && !item.pack && item.weightConfirmed
        )
      })

      // Use the centralized store method to handle weight confirmation, and status updates
      await kdsStore.updateOrder(props.order.id, updatedItems, status)

      if (status === 'complete' && props.onWeightConfirmed) {
        try {
          await props.onWeightConfirmed(props.order)
        } catch {
          // Reprint is best-effort — don't block the confirmation flow
        }
      }

      props.close()
    } catch (error: unknown) {
      // Error handling that works with both API error responses and regular errors
      setValidationError(
        error && typeof error === 'object' && 'data' in error
          ? (error as { data: unknown }).data
          : error
      )
    }
  }

  const returnErrorMessage = () => {
    if (!validationError) return 'An error occurred.'

    let errorMessage = 'An error occurred.'

    if (validationError.ErrorCode == 'UnconfirmedWeights') {
      errorMessage = 'Please confirm all weights before submitting.'
    }

    if (validationError.ErrorCode == 'OrderCannotBeReconcilled') {
      errorMessage = `${validationError.Context}. This order cannot be reconciled.`
    }

    if (validationError.Context) {
      errorMessage = validationError.Context
    }

    return errorMessage
  }

  return (
    <Portal>
      <Dialog
        toggleDialog={props.close}
        action={submitWeights}
        header="Confirm Weights"
        actionButtonLabel={'Confirm'}
        cancelButtonLabel={'Cancel'}
        customClass="weight-confirm-dialog"
      >
        <WeightDiscrepancyModal />
        <div className={`weight-confirm-modal`}>
          {validationError && <div className="fs-alert-error">{returnErrorMessage()}</div>}
          <div className={`weight-confirm-table`}>
            <table>
              <thead>
                <tr>
                  <th className="item-col">Items</th>
                  <th className="icon-col"></th>
                  <th className="requested-col">Requested</th>
                  <th className="final-col">Final Weight</th>
                </tr>
              </thead>
              <tbody>
                {items &&
                  items.map((item, index) => {
                    if (item.weightType != 'Fixed' && !item.pack) {
                      return (
                        <tr key={index}>
                          <td>{item.item}</td>
                          <td className="icon-column">
                            {isTempQtySet && (
                              <span
                                className={`icon icon-${
                                  item.weightConfirmed || !item.tempQty ? 'confirmed' : 'scale'
                                }`}
                              ></span>
                            )}
                          </td>
                          <td>{item.tempQty ? `${item.tempQty} ${item.unitType}` : '--'}</td>
                          <td className="final-weight">
                            <input
                              type="tel"
                              className="fs-input"
                              ref={elementsRef.current[index] as React.Ref<HTMLInputElement>}
                              onBlur={e =>
                                onWeightChange(e.target.value, item, elementsRef.current[index])
                              }
                              defaultValue={
                                !item.weightRequiresConfirmation && item.quantity
                                  ? item.quantity
                                  : ''
                              }
                            />
                            {item.unitType}
                          </td>
                        </tr>
                      )
                    }
                  })}
              </tbody>
            </table>
          </div>
        </div>
      </Dialog>
    </Portal>
  )
}
